import { Vector3 } from 'three';
import { Frame } from '../track/frames';
import { ProfileLoop } from './profile';

/** Indexed triangle mesh, renderer-agnostic — no Three.js Scene types in core. */
export interface SweepMesh {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
}

export interface SweepOptions {
  /** connect the last ring back to the first (closed circuit) */
  closeLoop?: boolean;
  /** cap the open ends so the solid is watertight (needed for printing) */
  capEnds?: boolean;
  /** anchor the profile at the rail line (default) or the heartline */
  anchor?: 'rail' | 'heart';
}

/**
 * Sweep a 2D profile loop along a sequence of banked frames and return an
 * indexed triangle solid. THE shared geometry path: the renderer calls it
 * low-res, the print exporter will call it high-res and capped (§11).
 */
export function sweepProfile(frames: Frame[], loop: ProfileLoop, opts: SweepOptions = {}): SweepMesh {
  const nF = frames.length;
  const nP = loop.points.length;
  const capEnds = opts.capEnds && !opts.closeLoop;
  const vertCount = nF * nP + (capEnds ? 2 * nP : 0);
  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);

  const pos = new Vector3();
  for (let i = 0; i < nF; i++) {
    const f = frames[i];
    const base = opts.anchor === 'heart' ? f.pos : f.railPos;
    for (let j = 0; j < nP; j++) {
      const pt = loop.points[j];
      pos.copy(base).addScaledVector(f.right, pt.x).addScaledVector(f.up, pt.y);
      const vi = (i * nP + j) * 3;
      positions[vi] = pos.x;
      positions[vi + 1] = pos.y;
      positions[vi + 2] = pos.z;
    }
  }

  const nSeg = opts.closeLoop ? nF : nF - 1;
  const quadCount = nSeg * nP;
  const capTris = capEnds ? 2 * (nP - 2) : 0;
  const indices = new Uint32Array(quadCount * 6 + capTris * 3);
  let ii = 0;
  for (let i = 0; i < nSeg; i++) {
    const a = i * nP;
    const b = ((i + 1) % nF) * nP;
    for (let j = 0; j < nP; j++) {
      const j1 = (j + 1) % nP;
      indices[ii++] = a + j;
      indices[ii++] = b + j;
      indices[ii++] = a + j1;
      indices[ii++] = a + j1;
      indices[ii++] = b + j;
      indices[ii++] = b + j1;
    }
  }

  if (capEnds) {
    // duplicate the end rings so the caps get flat normals
    const startBase = nF * nP;
    const endBase = startBase + nP;
    for (let j = 0; j < nP; j++) {
      copyVec(positions, (0 * nP + j) * 3, positions, (startBase + j) * 3);
      copyVec(positions, ((nF - 1) * nP + j) * 3, positions, (endBase + j) * 3);
    }
    // Fan triangulation (profiles are convex or near-convex).
    // Winding must oppose the tube's ring edges so the solid is orientable;
    // Mesh.merge() then welds the duplicated cap rings to the open ends.
    for (let j = 1; j < nP - 1; j++) {
      indices[ii++] = startBase;
      indices[ii++] = startBase + j;
      indices[ii++] = startBase + j + 1;
    }
    for (let j = 1; j < nP - 1; j++) {
      indices[ii++] = endBase;
      indices[ii++] = endBase + j + 1;
      indices[ii++] = endBase + j;
    }
  }

  computeNormals(positions, indices, normals);
  return { positions, normals, indices: indices.subarray(0, ii) as Uint32Array };
}

function copyVec(src: Float32Array, si: number, dst: Float32Array, di: number): void {
  dst[di] = src[si];
  dst[di + 1] = src[si + 1];
  dst[di + 2] = src[si + 2];
}

/** Merge several sweep meshes into one buffer set. */
export function mergeMeshes(meshes: SweepMesh[]): SweepMesh {
  let vCount = 0;
  let iCount = 0;
  for (const m of meshes) {
    vCount += m.positions.length / 3;
    iCount += m.indices.length;
  }
  const positions = new Float32Array(vCount * 3);
  const normals = new Float32Array(vCount * 3);
  const indices = new Uint32Array(iCount);
  let vOff = 0;
  let iOff = 0;
  for (const m of meshes) {
    positions.set(m.positions, vOff * 3);
    normals.set(m.normals, vOff * 3);
    for (let i = 0; i < m.indices.length; i++) indices[iOff + i] = m.indices[i] + vOff;
    vOff += m.positions.length / 3;
    iOff += m.indices.length;
  }
  return { positions, normals, indices };
}

/** Area-weighted smooth vertex normals. */
function computeNormals(positions: Float32Array, indices: Uint32Array, out: Float32Array): void {
  out.fill(0);
  const pA = new Vector3();
  const pB = new Vector3();
  const pC = new Vector3();
  const ab = new Vector3();
  const ac = new Vector3();
  const n = new Vector3();
  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i] * 3;
    const ib = indices[i + 1] * 3;
    const ic = indices[i + 2] * 3;
    pA.fromArray(positions, ia);
    pB.fromArray(positions, ib);
    pC.fromArray(positions, ic);
    ab.subVectors(pB, pA);
    ac.subVectors(pC, pA);
    n.crossVectors(ab, ac);
    for (const idx of [ia, ib, ic]) {
      out[idx] += n.x;
      out[idx + 1] += n.y;
      out[idx + 2] += n.z;
    }
  }
  for (let i = 0; i < out.length; i += 3) {
    const len = Math.hypot(out[i], out[i + 1], out[i + 2]);
    if (len > 1e-12) {
      out[i] /= len;
      out[i + 1] /= len;
      out[i + 2] /= len;
    }
  }
}
